/**
 * The create screen — the shell around whichever document is being created.
 *
 * There are two now, and they are different legal instruments rather than two
 * flavours of one: the goods DeCA (Orden FOM/2861/2012) and the passenger route
 * sheet (Orden FOM/1230/2013). They have different fields, different sections,
 * different rules about what the PDF prints, and their own endpoints. So this
 * screen keeps only what is common to both — the chrome, the `writeDecas` gate
 * and the selector — and hands everything else to one of two sibling forms,
 * each owning its own values, defaults and `toInput`.
 *
 * Goods is pre-selected, so a user who never touches the selector sees exactly
 * the screen they saw before it existed. A company offered one type (#220,
 * «Tipo de transporte») gets that type's form and no selector.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { offeredFamilies } from '../deca/transportType.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Card, NoAccessState } from '../ui/primitives.tsx'
import { GoodsCreateForm } from './GoodsCreateForm.tsx'
import { PassengerCreateForm } from './PassengerCreateForm.tsx'

export function DecaCreateScreen() {
  const { t } = useTranslation()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)
  const offered = offeredFamilies(principal)

  const [chosen, setTipo] = useState<'mercancias' | 'viajeros'>('mercancias')
  // A company offered one type gets its own form and no choice (#220).
  const tipo = !offered.goods ? 'viajeros' : !offered.passenger ? 'mercancias' : chosen

  if (!abilities.writeDecas) {
    return (
      <AppShell title={t('create.title')}>
        <Card>
          <NoAccessState />
        </Card>
      </AppShell>
    )
  }

  return (
    <AppShell title={t('create.title')}>
      {/* There is no SegmentedControl primitive, and the house pattern for a
          two-option choice is a pair of buttons carrying `aria-pressed` — which
          is what the grouping toggle inside `GoodsCreateForm` already does,
          with the same `.segmented` / `.segmented__option` rules. A primitive
          with one other caller is not worth the indirection. */}
      {offered.goods && offered.passenger ? (
        <div className="segmented" role="group" aria-label={t('create.tipoLabel')}>
          <button
            type="button"
            className="segmented__option"
            aria-pressed={tipo === 'mercancias'}
            onClick={() => setTipo('mercancias')}
          >
            {t('list.tipo.mercancias')}
          </button>
          <button
            type="button"
            className="segmented__option"
            aria-pressed={tipo === 'viajeros'}
            onClick={() => setTipo('viajeros')}
          >
            {t('list.tipo.viajeros')}
          </button>
        </div>
      ) : null}

      {tipo === 'mercancias' ? <GoodsCreateForm /> : <PassengerCreateForm />}
    </AppShell>
  )
}
